<template>
    <base-dialog :loading="loading" :value="dialog" :width="600" :fullscreen="isMobile" :title="$t('common.change_exercise')" @input="onInput" @close="hide">
        <v-row>
            <v-col cols="12" md="6">
                <kimsa-combobox :label="$t('common.category')" :placeholder="$t('exercises.pick_one_category')"
                    dense :items="categoriesOptions" @change="category = $event" :value="category" placeholder-class
                    item-text="label" ref="category" />
            </v-col>
            <v-col cols="12" md="6">
                <kimsa-combobox :label="$t('exercises.types')" :placeholder="$t('exercises.pick_type')" dense
                    :items="typesOptions" item-text="label" @change="types = $event" placeholder-class multiple-with-chips
                    :readonly="!category" :value="types" ref="types" />
            </v-col>
            <v-col cols="12">
                <kimsa-combobox can-write :label="$tc('exercises.exercises', 1)"
                    :placeholder="$t('exercises.pick_exercise')" dense :items="exercisesOptions"
                    @change="newExercise = $event" :value="newExercise" item-value="objectID" placeholder-class
                    ref="exercise" show-video @search="onSearchExercises" :no-create-exercise="true"
                    @showVideo="onShowVideo" />
            </v-col>
        </v-row>

        <watch-video-dialog @hide="onHideVideo" :thumb="currentImg" :video="currentVideo" is-second-dialog
            ref="watchVideo" />

        <template v-slot:footer>
            <v-btn tile block depressed x-large color="primary" class="black--text" :disabled="disabled"
                @click="changeExercise" :loading="loading">
                {{ $t('common.change') }}
            </v-btn>
        </template>
    </base-dialog>
</template>

<script>
import { searchExercises } from '@/utils/ExerciseLibrary'
import { mapState, mapActions } from "vuex";
import BaseDialog from './BaseDialog.vue';
import KimsaCombobox from './inputs/KimsaCombobox.vue';
import WatchVideoDialog from './routines/WatchVideoDialog.vue';
import { debounce } from "@/utils/Common";

export default {
    components: { BaseDialog, KimsaCombobox, WatchVideoDialog },
    name: "ChangeExerciseDialog",
    props: {
        exercise: {
            type: Object,
            default: null
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            dialog: false,
            loading: false,
            category: null,
            types: [],
            exercisesOptions: [],
            newExercise: null,
            currentImg: null,
            currentVideo: null,
            search: ''
        }
    },
    computed: {
        ...mapState(['categoriesOptions']),
        resolvedCoachId(vm = this) {
            return vm.coachId || vm.account?.studentSubscription?.coachId || null
        },
        typesOptions(vm = this) {
            if (!vm.category) return []
            return vm.category.types.map(type => ({ name: type, value: type, label: vm.typeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))
        },
        disabled(vm = this) {
            return !vm.newExercise
        }
    },
    async mounted() {
        const vm = this;
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
    },
    methods: {
        ...mapActions(['fetchExerciseCategories']),
        show() {
            const vm = this
            vm.dialog = true
            vm.setDefaults()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.category = null
            vm.types = []
            vm.newExercise = null
        },
        setDefaults() {
            const vm = this;
            if (!vm.exercise) return;
            const [categoryName] = vm.exercise.categories || []
            vm.category = vm.categoriesOptions.find(category => category.name === categoryName) || null
            vm.$nextTick(() => {
                if (!vm.category) return vm.types = []
                vm.types = (vm.exercise.types || []).map(type => ({ name: type, value: type, label: vm.typeLabel(type) }))
            })
        },
        async loadExercises() {
            const vm = this;
            if (vm.types.length === 0) return
            vm.exercisesOptions = []
            vm.exercisesOptions = await searchExercises({
                coachId: vm.resolvedCoachId,
                query: vm.search,
                types: vm.types,
            })
        },
        async onShowVideo(item) {
            const vm = this
            vm.currentImg = item.img || item.video?.thumb || null
            vm.currentVideo = item.video?.url || item?.youtubeUrl || null
            const isYoutubeVideo = item?.youtubeUrl ? true : false
            if (vm.$refs.exercise) vm.$refs.exercise.closeMenu()
            vm.$refs.watchVideo.show(isYoutubeVideo)
        },
        onHideVideo() {
            if (this.$refs.exercise) this.$refs.exercise.openMenu()
        },
        changeExercise() {
            const vm = this;
            if (!vm.exercise || !vm.newExercise) return
            vm.$emit('success', {oldExercise: vm.exercise, newExercise: vm.newExercise})
            vm.hide()
        },
        onSearchExercises(search) {
            const vm = this;
            vm.search = search || ''
            vm.debouncedLoadExercises()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 300),
    },
    watch: {

        category() {
            this.types = []
        },
        types() {
            const vm = this
            vm.loadExercises()
        },
        exercise(val) {
            if (val && this.dialog) this.setDefaults()
        }
    }
}
</script>